import {
  Box,
  Flex,
  Spinner,
  Stack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import type {
  VoucherEligibilityItem,
  VoucherGetByCodeResponse,
} from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import { useVoucherList, useVoucherStore } from '@diamond/sol-buyer-context';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import shallow from 'zustand/shallow';

import VoucherCard from './VoucherCard';
import {
  filterAvailableVouchers,
  filterDisabledVouchers,
} from './VoucherListSection';

type VoucherDisplayItem = VoucherEligibilityItem | VoucherGetByCodeResponse;

const VoucherList = () => {
  const router = useRouter();
  const toast = useToast();
  const { data, isLoading, error, fetchNextPage, isFetchingNextPage } =
    useVoucherList();
  const { ref: loadMoreTriggerRef, inView } = useInView();
  const pendingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Subscribe to values with shallow comparison
  const appliedVouchers = useVoucherStore(
    (state) => state.appliedVouchers,
    shallow
  );
  const pendingVouchers = useVoucherStore(
    (state) => state.pendingVouchers,
    shallow
  );

  // Get actions once (these don't cause re-renders)
  const voucherActions = useVoucherStore(
    (state) => ({
      addVoucher: state.addVoucher,
      removeVoucher: state.removeVoucher,
      addPendingVoucher: state.addPendingVoucher,
      removePendingVoucher: state.removePendingVoucher,
      clearPendingVouchers: state.clearPendingVouchers,
    }),
    shallow
  );

  // Simple helper functions - no need for Set or useCallback
  const isVoucherApplied = (code: string) =>
    appliedVouchers.some((v) => v.code === code);

  const isPendingVoucher = (code: string) => pendingVouchers.includes(code);

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (inView && !error) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView, error]);

  const allVouchers = data?.pages?.flatMap((page) => page.data) ?? [];
  const hasData = data?.pages && data.pages[0]?.data?.length > 0;

  const availableVouchers = filterAvailableVouchers(allVouchers);
  const disabledVouchers = filterDisabledVouchers(allVouchers);

  const handleApply = useCallback(
    (voucher: VoucherDisplayItem) => {
      const id = voucher.id;
      const code = voucher.code.trim();
      if (!id || !code) return;

      // Check if already processing
      if (pendingVouchers.includes(code)) return;

      voucherActions.addPendingVoucher(code);

      // Check if already applied
      const alreadyApplied = appliedVouchers.some((v) => v.code === code);

      if (alreadyApplied) {
        voucherActions.removeVoucher(code);
        showToast(
          toast,
          'success',
          'Voucher Berhasil Dihapus',
          undefined,
          'bottom',
          3000
        );
      } else {
        voucherActions.addVoucher(id, code);
        showToast(
          toast,
          'success',
          'Voucher Berhasil Digunakan',
          undefined,
          'bottom',
          3000
        );
      }

      // Clear previous timeout to prevent race conditions
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
      }

      pendingTimeoutRef.current = setTimeout(() => {
        voucherActions.removePendingVoucher(code);
        voucherActions.clearPendingVouchers();
        pendingTimeoutRef.current = null;
      }, 500);
    },
    [appliedVouchers, pendingVouchers, voucherActions, toast]
  );

  const handleCardClick = useCallback(
    (voucher: VoucherDisplayItem) => {
      router.push(`/voucher/${voucher.code}`);
    },
    [router]
  );

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner size="lg" color="#007AFF" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" flexDirection="column" align="center" minH="200px">
        <Image
          src="/images/voucher/voucher_error.png"
          alt="voucher error"
          width={216}
          height={216}
        />
        <Text
          color="red.500"
          fontSize="18px"
          fontWeight="bold"
          textAlign="center"
        >
          Ada Permasalahan Saat Menampilkan Voucher
        </Text>
      </Flex>
    );
  }

  if (!hasData) {
    return (
      <Flex justify="center" flexDirection="column" align="center" minH="200px">
        <Image
          src="/images/voucher/voucher_empty.png"
          alt="voucher empty"
          width={216}
          height={216}
        />
        <Text
          color="#8E8E93"
          fontSize="18px"
          fontWeight="bold"
          textAlign="center"
        >
          Tidak ada voucher yang tersedia
        </Text>
      </Flex>
    );
  }

  return (
    <>
      <VStack spacing="16px" align="flex-start" w="100%">
        <Stack spacing="12px" w="100%">
          <Text fontSize={{ base: '16px', lg: '20px' }} color="#000000">
            Tersedia
          </Text>
          {availableVouchers.length === 0 ? (
            <Flex
              justify="center"
              flexDirection="column"
              align="center"
              minH="200px"
            >
              <Image
                src="/images/voucher/voucher_empty.png"
                alt="voucher empty"
                width={216}
                height={216}
              />
              <Text fontSize="12px" fontWeight="bold" textAlign="center" mt="4">
                Tidak ada voucher yang tersedia
              </Text>
            </Flex>
          ) : (
            <Stack spacing="12px">
              {availableVouchers.map((voucher: VoucherEligibilityItem) => {
                const messageRuleProducts =
                  'message_rule_products' in voucher &&
                  voucher.message_rule_products
                    ? voucher.message_rule_products
                    : undefined;

                return (
                  <VoucherCard
                    key={voucher.id}
                    voucher={voucher}
                    onApply={handleApply}
                    onClick={handleCardClick}
                    isApplied={isVoucherApplied(voucher.code)}
                    isProcessing={isPendingVoucher(voucher.code)}
                    messageRuleProducts={messageRuleProducts}
                  />
                );
              })}
            </Stack>
          )}
        </Stack>

        {disabledVouchers.length > 0 && (
          <Stack spacing="12px" w="100%">
            <Text fontSize={{ base: '16px', lg: '20px' }} color="#000000">
              Belum dapat digunakan
            </Text>
            <Stack spacing="12px">
              {disabledVouchers.map((voucher: VoucherEligibilityItem) => {
                const messageRuleProducts =
                  'message_rule_products' in voucher &&
                  voucher.message_rule_products
                    ? voucher.message_rule_products
                    : undefined;

                return (
                  <VoucherCard
                    key={voucher.id}
                    voucher={voucher}
                    onClick={handleCardClick}
                    isDisabled
                    messageRuleProducts={messageRuleProducts}
                  />
                );
              })}
            </Stack>
          </Stack>
        )}
      </VStack>

      <Box
        ref={loadMoreTriggerRef}
        h="100px"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        {isFetchingNextPage && <Spinner size="lg" color="#007AFF" />}
      </Box>
    </>
  );
};

export default VoucherList;
